import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, KeyRound, X, Eye, EyeOff } from 'lucide-react';
import CandidateList from './CandidateList';
import OrganizationList from './OrganizationList';
import RequirementList from './RequirementList';
import SendInterview from './SendInterview';
import Campaigns from './Campaigns';
import LiveInterviews from './LiveInterviews';
import Reports from './Reports';
import ClientList from './ClientList';
import ProctorList from './ProctorList';
import InterviewerList from './InterviewerList';
import IntegrationsList from './IntegrationsList';
import Billing from './Billing';
import MIS from './MIS';
import BusinessUnits from './BusinessUnits';
import OrgSettings from './OrgSettings';
import Team from './Team';
import Analytics from './Analytics';
import Coupons from './Coupons';   // added 2026-07-16 from prepration-main merge — super-admin only
import { changeMyPassword } from '../services/api';

const LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;

// Two consoles from one codebase, served on separate subdomains:
//   admin.aietafirst.com      → REACT_APP_CONSOLE=admin     → super_admin only
//   customer.aietafirst.com  → REACT_APP_CONSOLE=recruiter → client_admin
//                                                             + recruiter
//                                                             + hiring_manager
// The Organizations / Clients / MIS / Business Units tabs list PLATFORM-WIDE
// data and are super_admin-only. The recruiter console exposes only what an
// org owner / their workforce should see about their own org.
const CONSOLE = (process.env.REACT_APP_CONSOLE || 'admin').toLowerCase();
const CONSOLE_TABS = {
  admin: ['organizations', 'clients', 'proctors', 'interviewers', 'integrations', 'billing', 'mis', 'analytics', 'business-units'],
  recruiter: ['org-settings', 'team', 'requirements', 'candidates', 'send-interview', 'campaigns', 'live-interviews', 'reports', 'billing', 'integrations'],
};
const CONSOLE_LABEL = { admin: 'Platform admin', recruiter: 'Workspace' };

// Decode JWT payload for role detection
const decodeToken = () => {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return {};
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return {}; }
};

// Tab definitions with role-based visibility.
// - "platform-wide" tabs (Organizations / Clients / MIS / Business Units) list
//   every tenant on AIETA First and are super_admin-only (Rupadi internal).
// - "org-scoped" tabs let a client_admin manage *only their own org*. The
//   backend list endpoints already filter by JWT.organization_id for
//   non-super-admin, so a client_admin loading Billing or Integrations sees
//   their own rows only.
// - "org-settings" + "team" are new client-admin tabs that replace the
//   misleading "Organizations" + "Clients" tabs the client used to land on.
const ALL_TABS = [
  // ── Platform-wide (super_admin only) ──
  { key: 'organizations',  label: 'Organizations',   roles: ['super_admin'] },
  { key: 'clients',        label: 'Clients',         roles: ['super_admin'] },
  { key: 'proctors',       label: 'Proctors',        roles: ['super_admin'] },
  { key: 'interviewers',   label: 'Interviewers',    roles: ['super_admin'] },
  { key: 'mis',            label: 'MIS',             roles: ['super_admin'] },
  { key: 'analytics',      label: 'Analytics',       roles: ['super_admin'] },
  { key: 'business-units', label: 'Business Units',  roles: ['super_admin', 'bu_head'] },
  { key: 'coupons',        label: 'Coupons',         roles: ['super_admin'] },  // 2026-07-16

  // ── Client workspace (customer.aietafirst.com) — org-scoped ──
  { key: 'org-settings',   label: 'Organization',    roles: ['client_admin'] },
  { key: 'team',           label: 'Team',            roles: ['client_admin'] },
  { key: 'integrations',   label: 'Integrations',    roles: ['super_admin', 'client_admin'] },
  { key: 'billing',        label: 'Billing',         roles: ['super_admin', 'client_admin'] },

  { key: 'requirements',   label: 'Requirements',    roles: ['client_admin', 'recruiter', 'hiring_manager'] },
  { key: 'candidates',     label: 'Candidates',      roles: ['client_admin', 'recruiter', 'hiring_manager'] },
  { key: 'send-interview', label: 'Send Interview',  roles: ['client_admin', 'recruiter'] },
  { key: 'campaigns',      label: 'Campaigns',       roles: ['client_admin', 'recruiter'] },
  { key: 'live-interviews',label: 'Live Interviews', roles: ['client_admin', 'recruiter'], pulse: true },
  { key: 'reports',        label: 'Reports',         roles: ['client_admin', 'recruiter', 'hiring_manager'] },
];

// Subdomain a given role should land on. Kept here (not in env) because the
// mapping is part of the platform's tenant-isolation perimeter — code review
// surfaces any change to it.
const ROLE_HOME = {
  super_admin:    process.env.REACT_APP_ADMIN_URL     || 'https://admin.aietafirst.com',
  bu_head:        process.env.REACT_APP_ADMIN_URL     || 'https://admin.aietafirst.com',
  client_admin:   process.env.REACT_APP_RECRUITER_URL || 'https://customer.aietafirst.com',
  recruiter:      process.env.REACT_APP_RECRUITER_URL || 'https://customer.aietafirst.com',
  hiring_manager: process.env.REACT_APP_RECRUITER_URL || 'https://customer.aietafirst.com',
};

function Dashboard() {
  const navigate = useNavigate();
  const currentUser = decodeToken();
  const userRole = currentUser.role || '';

  // Tenant-isolation gate: if a role lands on the wrong subdomain (admin
  // SPA for non-super-admin, or recruiter SPA for super_admin), bounce them
  // to where they belong. The current JWT is appended as a URL fragment so
  // the destination's bootstrapTokenFromFragment() picks it up — without
  // this, the cross-origin redirect would force them to log in again.
  React.useEffect(() => {
    if (!userRole) return;
    const expected = ROLE_HOME[userRole];
    if (expected && !expected.includes(window.location.hostname)) {
      const tok = localStorage.getItem('admin_token');
      const handoff = tok ? `#token=${encodeURIComponent(tok)}` : '';
      window.location.href = expected + '/dashboard' + handoff;
    }
  }, [userRole]);

  const visibleTabs = useMemo(() => {
    const allowed = CONSOLE_TABS[CONSOLE] || CONSOLE_TABS.admin;
    return ALL_TABS.filter(
      t => allowed.includes(t.key) && (t.roles === null || t.roles.includes(userRole))
    );
  }, [userRole]);

  const defaultTab = visibleTabs.length > 0 ? visibleTabs[0].key : 'organizations';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    try {
      setChangingPassword(true);
      await changeMyPassword({
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password,
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => { setShowChangePassword(false); setPasswordSuccess(''); }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950 text-ink-100">
      {/* Header */}
      <header className="border-b border-white/10 bg-ink-900/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Rupadi International" className="h-10 w-auto" />
            <div>
              <h1 className="text-lg font-extrabold tracking-tight text-white">AIETA First</h1>
              <p className="text-xs text-ink-400">{CONSOLE_LABEL[CONSOLE] || 'Admin'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
                setPasswordError(''); setPasswordSuccess(''); setShowChangePassword(true);
              }}
              className="btn-secondary px-3 py-2"
              title="Change Password"
            >
              <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">Change Password</span>
            </button>
            <button onClick={handleLogout} className="btn-secondary px-3 py-2">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs + content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="card overflow-hidden">
          <div className="border-b border-white/10">
            <nav className="flex -mb-px overflow-x-auto">
              {visibleTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap border-b-2 px-6 py-4 text-sm font-semibold transition-colors ${
                    activeTab === tab.key
                      ? 'border-brand-500 text-brand-400'
                      : 'border-transparent text-ink-400 hover:border-white/20 hover:text-ink-200'
                  }`}
                >
                  {tab.pulse ? (
                    <span className="flex items-center gap-2">
                      {tab.label}
                      <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400"></span>
                    </span>
                  ) : tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'organizations' && <OrganizationList />}
            {activeTab === 'clients' && <ClientList />}
            {activeTab === 'proctors' && <ProctorList />}
            {activeTab === 'interviewers' && <InterviewerList />}
            {activeTab === 'integrations' && <IntegrationsList />}
            {activeTab === 'billing' && <Billing />}
            {activeTab === 'mis' && <MIS />}
            {activeTab === 'analytics' && <Analytics />}
            {activeTab === 'business-units' && <BusinessUnits />}
            {activeTab === 'coupons' && <Coupons />}
            {activeTab === 'org-settings' && <OrgSettings />}
            {activeTab === 'team' && <Team />}
            {activeTab === 'requirements' && <RequirementList />}
            {activeTab === 'candidates' && <CandidateList />}
            {activeTab === 'send-interview' && <SendInterview />}
            {activeTab === 'campaigns' && <Campaigns />}
            {activeTab === 'live-interviews' && <LiveInterviews />}
            {activeTab === 'reports' && <Reports />}
          </div>
        </div>
      </main>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="card w-full max-w-md">
            <div className="flex items-center justify-between border-b border-white/10 p-4">
              <h3 className="text-lg font-semibold text-white">Change Password</h3>
              <button onClick={() => setShowChangePassword(false)} className="text-ink-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4 p-4">
              {passwordError && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{passwordError}</div>
              )}
              {passwordSuccess && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">{passwordSuccess}</div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-300">Current Password</label>
                <div className="relative">
                  <input
                    type={showOld ? 'text' : 'password'}
                    value={passwordForm.old_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                    className="input pr-10" required
                  />
                  <button type="button" onClick={() => setShowOld(!showOld)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-white">
                    {showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-300">New Password</label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="input pr-10" required
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-white">
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-300">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  className="input" required
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowChangePassword(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={changingPassword} className="btn-primary">
                  {changingPassword ? 'Changing…' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
